import {
  Box,
  BoxProps,
  Divider,
  IconButton,
  Stack,
  SxProps,
  TableCell,
  TextField,
  TextFieldProps,
  Theme,
  Tooltip,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { ArrowDownward, ArrowUpward, Clear, Search, Sort } from '@mui/icons-material';
import TooltipHardened from '../TooltipHardened';
import { ColumnDef } from '../DataTable';
import { MobileDatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import useDataTable from '../../Providers/DataTableProvider';

// Inputs sit one elevation above the page surface — bg matches
// background.paper, which is one step lighter than background.default in
// both modes (white on off-white in light, #1E1E1E on #121212 in dark).
// The slight lift makes the field read as an input without painting a
// contrasting tile.
const inputSx: SxProps<Theme> = {
  minWidth: '104px', // optimized for coordinate
  '& .MuiFilledInput-root': {
    backgroundColor: 'background.paper',
    '&:before': {
      borderColor: 'transparent',
      borderWidth: '1px',
    },
    '&:after': {
      borderColor: 'transparent',
      borderWidth: '1px',
    },
    '&.Mui-focused': {
      backgroundColor: 'background.paper',
      '&:before': {
        borderColor: 'transparent',
        borderWidth: '1px',
      },
    },
    '&:hover': {
      backgroundColor: 'background.paper',
      '&:before': {
        borderColor: 'transparent',
        borderWidth: '1px',
      },
    },
    '&:hover:not(.Mui-focused)': {
      '&:before': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
    },
    // Disabled: transparent background, no underline — looks like plain text.
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
      '&:before': {
        borderBottomStyle: 'none',
      },
    },
  },
};

const HeaderLabel = ({
  value,
  paddingLeft,
  divideLeft,
}: Pick<BoxProps, 'paddingLeft'> & { value: string; divideLeft?: boolean }) => (
  <Box sx={{ p: '2px 0px', display: 'flex', alignItems: 'center' }}>
    {divideLeft ? headerDivider : null}
    <Box sx={{ paddingLeft }}>{value}</Box>
  </Box>
);
const headerDivider = <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />;

interface Props {
  index: number;
  column: ColumnDef;
  sortDirection?: 'asc' | 'desc';
  onFilterInput?: (value: string) => void;
  onSortBy?: (direction?: 'asc' | 'desc') => void;
  onSearch?: () => void;
  onClear?: () => void;
}

export const DataTableHeader = ({
  index,
  column,
  sortDirection,
  onFilterInput,
  onSortBy,
  onSearch,
  onClear,
}: Props): React.JSX.Element => {
  // Read mutual-exclusivity and filter state directly from context so
  // changes don't cascade through the column definition pipeline.
  const { isSearchActive, committedFilter, fieldsFilter } = useDataTable();
  const filterDisabled = isSearchActive;
  const value = fieldsFilter[column.filterField ?? ''] ?? '';

  // Derive search/clear button state from committed filter:
  // show clear when a committed value exists and the user hasn't typed a new draft.
  const committedValue = committedFilter[column.filterField ?? ''] ?? '';
  const showClear = committedValue !== '' && (value === '' || value === committedValue);

  const doClear = useCallback(() => {
    onFilterInput?.('');
    onClear?.();
  }, [onFilterInput, onClear]);

  const doSearch = useCallback(() => {
    onSearch?.();
  }, [onSearch]);

  const searchButton = useMemo(() => {
    if (!showClear) {
      return (
        <IconButton type="button" sx={{ p: '10px' }} onClick={doSearch} disabled={filterDisabled}>
          <Search sx={filterDisabled ? { opacity: 0.3 } : undefined} />
        </IconButton>
      );
    } else {
      return (
        <IconButton type="button" sx={{ p: '10px' }} onClick={doClear} disabled={filterDisabled}>
          <Clear sx={filterDisabled ? { opacity: 0.3 } : undefined} />
        </IconButton>
      );
    }
  }, [showClear, doClear, doSearch, filterDisabled]);

  const sortButton = useMemo(() => {
    if (onSortBy === undefined) {
      return <></>;
    }
    const prettySortDirection = sortDirection === 'desc' ? 'Ascending' : 'Descending';
    const renderIcon = (): React.JSX.Element => {
      if (sortDirection === 'asc') {
        return <ArrowUpward />;
      } else if (sortDirection === 'desc') {
        return <ArrowDownward />;
      } else {
        return <Sort />;
      }
    };

    return (
      <Tooltip placement={'top'} title={`Sort by ${column.header}: ${prettySortDirection}`}>
        <div>
          <IconButton
            onClick={() => {
              onSortBy(sortDirection === 'desc' ? 'asc' : 'desc');
            }}
          >
            {renderIcon()}
          </IconButton>
        </div>
      </Tooltip>
    );
  }, [onSortBy, sortDirection, column.header]);

  const inputElement = useMemo(() => {
    const { filterInputOptions } = column;
    const label = <HeaderLabel value={column.header} />;

    const sharedProps: TextFieldProps = {
      fullWidth: true,
      disabled: filterDisabled,
      label,
      variant: 'filled',
      sx: inputSx,
      onKeyDown: (event) => {
        if (event.key === 'Enter') {
          doSearch();
        }
      },
    };

    switch (filterInputOptions?.variant) {
      case 'json': {
        return (
          <TextField
            {...sharedProps}
            value={value}
            onBlur={() => {
              if (value.trim() === '{}=') {
                onFilterInput?.('');
              }
            }}
            onClick={() => {
              if (value === '') {
                onFilterInput?.('{}=');
              }
            }}
            onChange={(e) => {
              let inputString = e.target.value as string;
              if (inputString !== '') {
                let [path, val] = inputString.split('=');
                if (val === undefined) {
                  val = '';
                }

                if (!path.startsWith('{')) {
                  if (path.includes('{')) {
                    path = path.replace('{', '');
                  }
                  path = '{' + path;
                }
                if (!path.endsWith('}')) {
                  if (path.includes('}')) {
                    path = path.replace('}', '');
                  }
                  path += '}';
                }

                inputString = path + '=' + val;
              }

              onFilterInput?.(inputString);
            }}
          />
        );
      }
      case 'date': {
        return (
          <MobileDatePicker
            disableFuture
            value={value !== '' ? moment(value) : null}
            onChange={(date: Moment | null) => {
              onFilterInput?.(date ? date.format('YYYY-MM-DD') : '');
            }}
            format={'YYYY-MM-DD'}
            onAccept={() => doSearch()}
            slotProps={{
              textField: {
                ...sharedProps,
              },
            }}
          />
        );
      }
      case 'text':
      default:
        return (
          <TextField
            {...sharedProps}
            value={value}
            onChange={(e) => {
              onFilterInput?.(e.target.value as string);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                doSearch();
              }
            }}
          />
        );
    }
  }, [column.header, column.filterInputOptions, value, onFilterInput, doSearch, filterDisabled]);

  const searchHeader = useMemo(
    () => (
      <TooltipHardened placement={'top'} tooltip={column.searchTooltip}>
        <Box sx={{ p: '2px 0px', display: 'flex', alignItems: 'center', width: '100%' }}>
          {index !== 0 ? headerDivider : null}
          {sortButton}
          {onSortBy && headerDivider}
          {searchButton}
          {inputElement}
        </Box>
      </TooltipHardened>
    ),
    [index, column.searchTooltip, inputElement, sortButton, searchButton],
  );

  return (
    <TableCell key={index}>
      <Stack direction={'row'} spacing={1} alignItems={'center'}>
        {onFilterInput ? (
          searchHeader
        ) : (
          <HeaderLabel value={column.header} paddingLeft={'10px'} divideLeft={index !== 0} />
        )}
      </Stack>
    </TableCell>
  );
};
